import React__default, { useState, useEffect, useContext } from 'react';
import { u as useAuth, A as AppAuthScreen } from './AppAuthScreen-c5cc40a9.esm.js';

var _window;
const isElectron = !!((_window = window) !== null && _window !== void 0 && _window.electronAPI);
const initialValues = {
  user: null,
  isIncognito: null,
  setIsIncognito: v => {},
  updateUser: u => {},
  fetchUser: async () => {}
};
const UserContext = /*#__PURE__*/React__default.createContext(initialValues);
// new line custom hook

const UserProvider = _ref => {
  let {
    children,
    onUserSessionAuthenticated,
    cognitoUserMapper
  } = _ref;
  const [user, setUser] = useState({
    userNickname: ""
  });
  const [isIncognito, setIsIncognito] = useState(null);
  const {
    getCurrentAuthenticatedUser,
    currentView
  } = useAuth();
  const updateUser = u => {
    setUser(u);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchUser = async () => {
    if (isElectron) {
      const claims = await AppAuthScreen.getCurrentAuthenticatedUser();
      if (!claims) {
        console.warn("fetchUser: No authenticated user claims found.");
        // Potentially clear user state if needed
        // setUser(null); // Or your equivalent reset action
        // Potentially call onUserSessionAuthenticated(null) if appropriate
        onUserSessionAuthenticated && onUserSessionAuthenticated(null); // Notify listener
        return; // Exit if no user
      }

      console.log("fetchUser: Received claims:", claims);

      // --- Call the original listener (optional) ---
      // NOTE: onUserSessionAuthenticated previously received the Amplify _user object.
      // Now it receives the claims object. You might need to update the listener
      // OR pass a reconstructed object if the listener absolutely needs the old format.
      // Passing claims directly is usually cleaner.
      onUserSessionAuthenticated && onUserSessionAuthenticated(claims);

      // --- Determine user attributes based on claims ---
      // The 'sub' claim is the most reliable unique ID from Cognito
      const username = claims.sub; // Use 'sub' as the primary identifier

      // Email and verification are standard claims
      const email = claims.email;
      const emailVerified = claims.email_verified === true; // Ensure boolean check

      // Phone number might not always be present
      const phoneNumber = claims.phone_number || null;
      const phoneNumberVerified = claims.phone_number_verified === true;

      // You could check for federation specifically if needed for other logic,
      // but the claims usually provide the core attributes regardless.
      // const isFederated = !!claims.identities;
      // const providerName = claims.identities?.[0]?.providerName;

      // Set the user state using the extracted claims
      setUser({
        username: username,
        user_email: email,
        user_email_verified: emailVerified,
        user_phone_number: phoneNumber,
        user_phone_number_verified: phoneNumberVerified
        // You can add other relevant claims here if needed by your state
        // e.g., name: claims.name,
        // cognitoUsername: claims['cognito:username'] // If specifically needed
      });

      console.log("fetchUser: User state updated with claims.");
    } else {
      var _user$username, _user$username2;
      const _user = await getCurrentAuthenticatedUser();
      onUserSessionAuthenticated && onUserSessionAuthenticated(_user);
      // manage if _user is coming from google or cognito
      if (_user !== null && _user !== void 0 && (_user$username = _user.username) !== null && _user$username !== void 0 && _user$username.startsWith("google_") || _user !== null && _user !== void 0 && (_user$username2 = _user.username) !== null && _user$username2 !== void 0 && _user$username2.startsWith("azure")) {
        var _user$signInUserSessi, _user$signInUserSessi2, _user$signInUserSessi3, _user$signInUserSessi4, _user$signInUserSessi5, _user$signInUserSessi6;
        setUser({
          username: _user === null || _user === void 0 ? void 0 : (_user$signInUserSessi = _user.signInUserSession) === null || _user$signInUserSessi === void 0 ? void 0 : (_user$signInUserSessi2 = _user$signInUserSessi.idToken) === null || _user$signInUserSessi2 === void 0 ? void 0 : (_user$signInUserSessi3 = _user$signInUserSessi2.payload) === null || _user$signInUserSessi3 === void 0 ? void 0 : _user$signInUserSessi3.sub,
          user_email: _user === null || _user === void 0 ? void 0 : (_user$signInUserSessi4 = _user.signInUserSession) === null || _user$signInUserSessi4 === void 0 ? void 0 : (_user$signInUserSessi5 = _user$signInUserSessi4.idToken) === null || _user$signInUserSessi5 === void 0 ? void 0 : (_user$signInUserSessi6 = _user$signInUserSessi5.payload) === null || _user$signInUserSessi6 === void 0 ? void 0 : _user$signInUserSessi6.email,
          user_email_verified: true,
          user_phone_number: null,
          user_phone_number_verified: false
        });
      } else {
        setUser({
          username: _user.username,
          user_email: _user.attributes.email,
          user_email_verified: _user.attributes.email_verified,
          user_phone_number: _user.attributes.phone_number,
          user_phone_number_verified: _user.attributes.phone_number_verified
        });
      }
    }
  };
  useEffect(() => {
    if (user.isUserOnBoarded) setIsIncognito(false);
  }, [user.isUserOnBoarded]);
  useEffect(() => {
    if (currentView === "mainNav") fetchUser();
  }, [currentView]);
  return /*#__PURE__*/React__default.createElement(UserContext.Provider, {
    value: {
      user: user,
      isIncognito: isIncognito,
      setIsIncognito: setIsIncognito,
      updateUser: updateUser,
      fetchUser: fetchUser
    }
  }, children);
};
const useUser = () => {
  const {
    user,
    isIncognito,
    setIsIncognito,
    updateUser,
    fetchUser
  } = useContext(UserContext);
  return {
    user,
    isIncognito,
    setIsIncognito,
    updateUser,
    fetchUser
  };
};
UserProvider.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "UserProvider"
};

export { UserProvider as U, useUser as u };
